import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { type SingularKeys, useTrans } from '@libs/i18n';
import { ArrowRightIcon } from '@phosphor-icons/react';

import { type HubDisplayStatus, TONE_SX } from './documentStatusMeta';

export type StatusCounts = Record<HubDisplayStatus, number>;

interface Props {
  counts: StatusCounts;
  onReviewAll?: () => void;
}

interface StripCard {
  status: HubDisplayStatus;
  labelKey: SingularKeys;
  tone: keyof typeof TONE_SX;
}

/** Fixed status summary cards, in the order shown in the design. */
const CARDS: StripCard[] = [
  { status: 'pending_review', labelKey: 'app.documentHub.strip.pendingReview', tone: 'amber' },
  { status: 'extracting', labelKey: 'app.documentHub.strip.extracting', tone: 'blue' },
  { status: 'done', labelKey: 'app.documentHub.strip.done', tone: 'green' },
  { status: 'failed', labelKey: 'app.documentHub.strip.failed', tone: 'red' },
];

/**
 * The row of count cards above the table (Pending review / Extracting / Done /
 * Failed). These are read-only summary cards; filtering is done via the separate
 * filter controls below. The "pending review" card exposes a "Review all"
 * shortcut.
 */
export const DocumentStatusStrip: FC<Props> = observer(
  ({ counts, onReviewAll }) => {
    const { t } = useTrans();

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {CARDS.map((card) => {
          const tone = TONE_SX[card.tone];
          const count = counts[card.status] ?? 0;
          const showReviewAll =
            card.status === 'pending_review' && count > 0 && onReviewAll;

          return (
            <Box
              key={card.status}
              sx={{
                flex: '1 1 0',
                minWidth: 150,
                p: 1.75,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                border: 1,
                borderColor: 'grey.300',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flex: 'none',
                    backgroundColor: tone.fg,
                  }}
                />
                <Typography variant="textXS_semiBold" color="text.tertiary">
                  {t(card.labelKey)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="text2XL_bold" color={tone.fg}>
                  {count}
                </Typography>
              </Box>
              {showReviewAll && (
                <Box
                  component="button"
                  type="button"
                  onClick={() => onReviewAll?.()}
                  sx={{
                    alignSelf: 'flex-start',
                    p: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    typography: 'textXS_semiBold',
                    color: tone.fg,
                  }}
                >
                  {t('app.documentHub.strip.reviewAll')}
                  <ArrowRightIcon size={12} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  },
);
